import {
  type Basket,
  type ServicesPreferencesModalProps,
} from '@wix/bookings-services-preferences-modal/types';
import { anyGenderStaffOverrides } from '@wix/bookings-services-preferences-modal/utils';

import { WidgetName } from '@wix/bookings-calendar-catalog-viewer-mapper';
import { type ISettingsParam } from '@wix/tpa-settings';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import {
  useBi,
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import loadable from '@wix/yoshi-flow-editor/loadable';
import React from 'react';
import { DialogViewModel } from '../../../../service-page-view-model/dialogViewModel/dialogViewModel';
import settingsParams, { withoutDefaultFallback } from '../../settingsParams';
import stylesParams from '../../stylesParams';
import { useServicePageActions } from '../useServicePageActions';
import { servicesPreferencesModalKeysMap as servicesPreferencesModalFormatterKeysMap } from './constants';
import { classes } from './Dialog.st.css';

export type DialogProps = {
  viewModel: DialogViewModel;
};

const ServicesPreferencesModal: React.FC<ServicesPreferencesModalProps> =
  loadable(() => import('@wix/bookings-services-preferences-modal'));

export const Dialog: React.FC<DialogProps> = ({ viewModel }) => {
  const { experiments } = useExperiments();
  const { closeDialog, onPreferencesModalServiceSelected, navigateToCalendar } =
    useServicePageActions();
  const { t } = useTranslation();
  const { isMobile, isRTL, isCssPerBreakpoint } = useEnvironment();
  const styles = useStyles();
  const biLogger = useBi();
  const settings = useSettings();
  const ownerText = (settingsParam: ISettingsParam) =>
    settings.get(withoutDefaultFallback(settingsParam)) || undefined;

  if (viewModel.isOpen) {
    return (
      <ServicesPreferencesModal
        className={classes.servicesPreferencesModal}
        {...viewModel.data!}
        widgetStyles={{
          primaryColor: styles.get(stylesParams.bodyButtonBackgroundColor)
            .value,
          discountNameColor: styles.get(stylesParams.discountNameColor).value,
          priceBeforeDiscountColor: styles.get(stylesParams.priceBeforeDiscountColor).value,
        }}
        t={t}
        onConfirm={(basket: Basket) => {
          closeDialog();
          navigateToCalendar({ basket });
        }}
        onClose={closeDialog}
        onServiceSelected={onPreferencesModalServiceSelected}
        isMobile={isMobile}
        isRTL={isRTL}
        isCssPerBreakpoint={isCssPerBreakpoint}
        biLogger={biLogger}
        formatterKeysMap={servicesPreferencesModalFormatterKeysMap}
        formatterOverrides={anyGenderStaffOverrides({
          femaleLabel: ownerText(settingsParams.anyFemaleStaffLabel),
          maleLabel: ownerText(settingsParams.anyMaleStaffLabel),
        })}
        experiments={experiments}
        openedBy={WidgetName.SERVICE_PAGE}
      />
    );
  }

  return null;
};
